import VerifiedIcon from "@mui/icons-material/Verified";
import { Avatar, IconButton } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import ProfileModal from "../../../components/ProfileModal";
import { useAppSelector } from "../../../hooks";
import useBasicProfile from "../../../hooks/useBasicProfile";
import useCryptography from "../../../hooks/useCryptography";
import {
  initRoomInfomation,
  setLastDMTarget,
} from "../../../store/reducers/dmState";
import { TMessage } from "../../../types/types";
import { getProticoClient } from "../../../utils/api";
import { checkDM } from "../../../utils/dm.utils";
import { notEmpty, truncateUsername } from "../../../utils/format.utils";
import NftBuyModal from "../../NftBuyModal";
import NftListModal from "../../NftListModal";
import ChatInput from "./ChatInput";
import ChatItem from "./ChatItem";
import ChatTyping from "./ChatTyping";
import Blockies from "react-blockies";

type Prop = {
  toAddress: string;
  alias?: string;
};

const Chat: React.FC<Prop> = ({ toAddress, alias }) => {
  const lowerCaseToAddress = toAddress.toLowerCase();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [profileModalVisible, setProfileModalVisible] = useState(false);
  const { roomInformations } = useAppSelector((state) => state.dmState);
  const { basicProfile } = useBasicProfile(toAddress);
  const { currentAddress, authenticateStatus } = useAppSelector(
    (state) => state.auth
  );
  const [listModalstatus, setlistModalstatus] = useState(false);
  const [assetstatus, setAsset] = useState({
    imageUrl: "",
    permalink: "",
    name: "",
    tokenId: 0,
    assetContract: "",
    itemType: "",
  });
  const [buyModalstatus, setbuyModalstatus] = useState({
    messageid: "",
    status: false,
    setbuyModalstatus: "",
    data: {},
    type: "dm",
  });
  const { secondFontColor } = useAppSelector((state) => state.layout);
  const [targetPubkey, setTargetPubkey] = useState<Uint8Array>();
  const [isPending, setIsPending] = useState<boolean>(true);
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const mainRoomInfo = roomInformations[lowerCaseToAddress]; //TODO: this might be undefined when it is not loaded
  const isOtherTyping = mainRoomInfo?.isOtherTyping;
  /** isOtherJoined is true when the target is looking at chat*/
  const { decrypt } = useCryptography();
  const messageList = useMemo(() => {
    if (!mainRoomInfo) return [];
    let messages: Array<TMessage> = [];
    for (const [id, message] of Object.entries(mainRoomInfo.messageList)) {
      messages.push(message);
    }
    messages.sort(
      (a, b) =>
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    );
    return messages;
  }, [mainRoomInfo?.messageList]);
  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" });
  };
  useEffect(() => {
    if (toAddress) dispatch(setLastDMTarget({ toAddress, alias }));
  }, [toAddress]);

  useEffect(() => {
    // TODO: get history
    getProticoClient()
      .directMessages.getHistory(currentAddress, toAddress)
      .then((historyMessageList) => {
        const processedMessages = historyMessageList
          .map((historyMessage) => {
            try {
              const plaintextMessage = decrypt(historyMessage);
              return {
                ...historyMessage,
                plaintextMessage: plaintextMessage,
                sended: true,
              } as TMessage;
            } catch (_) {
              return undefined;
            }
          })
          .filter(notEmpty);
        dispatch(
          initRoomInfomation({
            toAddress,
            historyMessageList: processedMessages,
          })
        );
      });
  }, []);
  useEffect(() => {
    setIsPending(true);
    checkDM(lowerCaseToAddress)
      .then((key) => {
        if (key) {
          setTargetPubkey(key);
          setIsPending(false);
        }
      })
      .catch((e) => setIsPending(false));
  }, [authenticateStatus, currentAddress, lowerCaseToAddress]);

  const [isInitScroll, setIsInitScroll] = useState(false);

  useEffect(() => {
    if (isInitScroll) scrollToBottom();
    setTimeout(() => {
      setIsInitScroll(true);
      scrollToBottom();
    }, 1000);
  }, [messageList]);

  const getMessageView = () => {
    if (!basicProfile)
      return (
        <div
          className="chat-empty-message-text"
          style={{ color: secondFontColor }}
        >
          {t("DM_Connecting")}
        </div>
      );
    if (messageList.length <= 0 && !isOtherTyping)
      return (
        <div
          className="chat-empty-message-text"
          style={{ color: secondFontColor }}
        >
          {t("DM_Start_1")}
          <br />
          {t("DM_Start_2")}
        </div>
      );
    return (
      <div>
        {messageList.map((message, index) => (
          <ChatItem
            key={`ChatItem_${index}`}
            profileData={basicProfile}
            message={message}
            toAddress={toAddress}
            scrollToBottom={scrollToBottom}
            setbuyModalstatus={setbuyModalstatus}
          />
        ))}
        {isOtherTyping && (
          <ChatTyping profileData={basicProfile} toAddress={toAddress} />
        )}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  const getAddress = () => {
    if (!basicProfile) return;
    if (basicProfile.isENSVerified) return basicProfile.name;
    return toAddress;
  };

  const getview = () => {
    if (!mainRoomInfo || isPending)
      return (
        <div className="body-container">
          <div className="dot-falling" />
        </div>
      );
    if (!targetPubkey)
      return (
        <div className="body-container">
          {t("DM_Incompleted_Register_of_The_Other_User")}
        </div>
      );
    return (
      <>
        {profileModalVisible && (
          <ProfileModal
            address={toAddress}
            uuid={toAddress}
            setProfileModalVisible={setProfileModalVisible}
          />
        )}
        <div className="chat-item-user-info">
          <IconButton
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={() => {
              setProfileModalVisible(true);
            }}
            sx={{ p: 0, margin: "0 10px" }}
            color="inherit"
          >
            {basicProfile.avatar_url ? (
              <Avatar
                alt="PFP"
                src={basicProfile.avatar_url}
                sx={{ width: 25, height: 25 }}
              />
            ) : (
              <Blockies
                seed={toAddress}
                size={10}
                scale={2.5}
                color="#ddd"
                className="identicon"
              />
            )}
          </IconButton>
          <div className="chat-item-user-name">
            {truncateUsername(alias ? alias : basicProfile.name ?? toAddress)} (
            {truncateUsername(getAddress())})
            {!!basicProfile.isENSVerified && (
              <VerifiedIcon
                color="primary"
                fontSize="small"
                style={{
                  verticalAlign: "middle",
                  margin: "-1px 0 0 4px",
                }}
              />
            )}
          </div>
        </div>
        <ul className="chat-message-list" id="messagelist">
          {getMessageView()}
        </ul>
        <ChatInput
          lowerCaseToAddress={lowerCaseToAddress}
          isChatting={!!mainRoomInfo}
          targetPubkey={targetPubkey}
          setlistModalstatus={setlistModalstatus}
          setAsset={setAsset}
        />
        <NftListModal
          isOpen={listModalstatus}
          onClose={() => setlistModalstatus(false)}
          assetstatus={assetstatus}
          targetPubkey={targetPubkey}
          lowerCaseToAddress={lowerCaseToAddress}
        />
        <NftBuyModal
          buyModalstatus={buyModalstatus}
          onClose={() =>
            setbuyModalstatus({ ...buyModalstatus, status: false })
          }
        />
      </>
    );
  };
  return getview();
};

export default Chat;
